import type { OrganizationProfileData, OrgType, ProfileData } from "../profile";
import {
  finiteNonNegativeEncounterVolume,
  isPrimaryEncounterKey,
} from "../profile";

// ── Archetype Shape ──────────────────────────────────────────

interface ArchetypeDefaults {
  /**
   * Primary buckets — fractions of the total encounter volume. Inpatient and
   * outpatient partition every encounter, so these must sum to 1.0.
   */
  primary_volume_distribution: { inpatient: number; outpatient: number };
  /**
   * Sub-breakdowns generated independently as subsets of a primary bucket.
   * `share` is the fraction OF THE PARENT bucket (not of the total), so the
   * total is always just inpatient + outpatient — sub-breakdowns never inflate
   * it.
   */
  sub_breakdown_distribution: Record<
    string,
    { parent: "inpatient" | "outpatient"; share: number }
  >;
  specialty_mix: Record<string, number>;
  payer_mix: Record<string, number>;
  workforce_ratios: Record<
    string,
    { ftes_per_1000_encounters: number; avg_cost: number }
  >;
}

export interface DerivationExplanation {
  field: string;
  value: number;
  explanation: string;
}

function hasEncounterVolumeInput(
  encounterVolumes: ProfileData["encounter_volumes"] | undefined,
  key: string,
): boolean {
  return finiteNonNegativeEncounterVolume(encounterVolumes?.[key]) != null;
}

/**
 * Flatten an archetype's encounter distribution into fractions OF TOTAL for
 * every setting (primary buckets + sub-breakdowns). A sub-breakdown's
 * fraction-of-total is its parent's fraction × its within-parent share. Used
 * by estimation routines that weight benchmarks by setting mix.
 */
export function archetypeEncounterDistribution(
  archetype: ArchetypeDefaults,
): Record<string, number> {
  const dist: Record<string, number> = {
    ...archetype.primary_volume_distribution,
  };
  for (const [sub, { parent, share }] of Object.entries(
    archetype.sub_breakdown_distribution,
  )) {
    dist[sub] = (archetype.primary_volume_distribution[parent] ?? 0) * share;
  }
  return dist;
}

/**
 * Build a *mutually-exclusive* encounter distribution (fractions of total that
 * sum to 1.0) suitable for weighting per-setting benchmarks. Each sub-breakdown
 * is carved out of its parent bucket, leaving a parent "residual" for the
 * portion not attributed to any sub-breakdown. Because no encounter is counted
 * twice, the weights form a true average — unlike
 * {@link archetypeEncounterDistribution}, whose primary buckets and
 * sub-breakdowns deliberately overlap.
 */
export function archetypeLeafEncounterDistribution(
  archetype: ArchetypeDefaults,
): Record<string, number> {
  const leaves: Record<string, number> = {
    ...archetype.primary_volume_distribution,
  };
  for (const [sub, { parent, share }] of Object.entries(
    archetype.sub_breakdown_distribution,
  )) {
    const subFractionOfTotal =
      (archetype.primary_volume_distribution[parent] ?? 0) * share;
    leaves[sub] = subFractionOfTotal;
    // Carve the sub-breakdown out of its parent so the residual + sub-breakdowns
    // stay mutually exclusive and still sum to the parent's share.
    leaves[parent] = (leaves[parent] ?? 0) - subFractionOfTotal;
  }
  // Guard against tiny negative residuals from rounded shares.
  for (const key of Object.keys(leaves)) {
    if ((leaves[key] ?? 0) < 0) leaves[key] = 0;
  }
  return leaves;
}

function applyEncounterVolumeInputs(input: {
  baseVolumes: Record<string, number>;
  encounterVolumeInputs: ProfileData["encounter_volumes"] | undefined;
  totalEncounters: number;
}): Record<string, number> {
  const { baseVolumes, encounterVolumeInputs, totalEncounters } = input;
  if (!encounterVolumeInputs) return baseVolumes;

  const next = { ...baseVolumes };
  // Only the primary buckets (inpatient / outpatient) partition the total, so
  // only they are redistributed against it. Sub-breakdowns are independent
  // subsets and are applied verbatim below.
  const additiveKeys = Object.keys(baseVolumes).filter(isPrimaryEncounterKey);
  const explicitAdditiveKeys = additiveKeys.filter((key) =>
    hasEncounterVolumeInput(encounterVolumeInputs, key),
  );

  if (explicitAdditiveKeys.length > 0) {
    const explicitSum = explicitAdditiveKeys.reduce(
      (sum, key) =>
        sum +
        (finiteNonNegativeEncounterVolume(encounterVolumeInputs[key]) ?? 0),
      0,
    );
    const remaining = Math.max(0, totalEncounters - explicitSum);
    const redistributedKeys = additiveKeys.filter(
      (key) => !explicitAdditiveKeys.includes(key),
    );
    const baseRemaining = redistributedKeys.reduce(
      (sum, key) => sum + (baseVolumes[key] ?? 0),
      0,
    );

    for (const key of explicitAdditiveKeys) {
      next[key] =
        finiteNonNegativeEncounterVolume(encounterVolumeInputs[key]) ?? 0;
    }

    for (const key of redistributedKeys) {
      next[key] =
        baseRemaining > 0
          ? Math.round(remaining * ((baseVolumes[key] ?? 0) / baseRemaining))
          : 0;
    }

    if (explicitSum <= totalEncounters && redistributedKeys.length > 0) {
      const roundingAdjustmentKey = redistributedKeys[0];
      if (!roundingAdjustmentKey) return next;
      const additiveTotal = additiveKeys.reduce(
        (sum, key) => sum + (next[key] ?? 0),
        0,
      );
      next[roundingAdjustmentKey] =
        (next[roundingAdjustmentKey] ?? 0) + (totalEncounters - additiveTotal);
    }
  }

  for (const [key, value] of Object.entries(encounterVolumeInputs)) {
    if (isPrimaryEncounterKey(key)) continue;
    const normalized = finiteNonNegativeEncounterVolume(value);
    if (normalized != null) {
      next[key] = normalized;
    }
  }

  return next;
}

// ── Archetype Definitions ────────────────────────────────────
//
// Inpatient + outpatient are the two top-level buckets and sum to 1.0.
// Sub-breakdowns (emergency / ambulatory / radiology) are expressed as a share
// OF THEIR PARENT bucket so they are generated as subsets, never added to the
// total. Outpatient is the umbrella for all non-admitted encounters, so the
// outpatient-leaning sub-breakdowns hang off it; the parents here match the
// editor's default parents.

export const ARCHETYPES: Record<OrgType, ArchetypeDefaults> = {
  academic_medical_center: {
    primary_volume_distribution: { inpatient: 0.05, outpatient: 0.95 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.0947 },
      ambulatory: { parent: "outpatient", share: 0.4421 },
      radiology: { parent: "outpatient", share: 0.1895 },
    },
    specialty_mix: {
      primary_care: 0.25,
      radiology: 0.15,
      surgery: 0.1,
      emergency_medicine: 0.12,
      cardiology: 0.08,
      oncology: 0.06,
      orthopedics: 0.05,
      other: 0.19,
    },
    payer_mix: {
      medicare_ffs: 0.18,
      medicare_advantage: 0.22,
      commercial: 0.35,
      medicaid: 0.15,
      medicaid_managed: 0.05,
      self_pay: 0.05,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.052, avg_cost: 70000 },
      pa_staff: { ftes_per_1000_encounters: 0.026, avg_cost: 55000 },
      call_center_agent: { ftes_per_1000_encounters: 0.042, avg_cost: 45000 },
      physician: { ftes_per_1000_encounters: 0.208, avg_cost: 350000 },
      nurse: { ftes_per_1000_encounters: 0.833, avg_cost: 85000 },
    },
  },

  community_health_system: {
    primary_volume_distribution: { inpatient: 0.06, outpatient: 0.94 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.1277 },
      ambulatory: { parent: "outpatient", share: 0.4043 },
      radiology: { parent: "outpatient", share: 0.1489 },
    },
    specialty_mix: {
      primary_care: 0.35,
      radiology: 0.1,
      surgery: 0.08,
      emergency_medicine: 0.15,
      cardiology: 0.07,
      oncology: 0.04,
      orthopedics: 0.04,
      other: 0.17,
    },
    payer_mix: {
      medicare_ffs: 0.22,
      medicare_advantage: 0.18,
      commercial: 0.3,
      medicaid: 0.18,
      medicaid_managed: 0.06,
      self_pay: 0.06,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.048, avg_cost: 65000 },
      pa_staff: { ftes_per_1000_encounters: 0.024, avg_cost: 52000 },
      call_center_agent: { ftes_per_1000_encounters: 0.038, avg_cost: 42000 },
      physician: { ftes_per_1000_encounters: 0.19, avg_cost: 300000 },
      nurse: { ftes_per_1000_encounters: 0.75, avg_cost: 78000 },
    },
  },

  critical_access_hospital: {
    primary_volume_distribution: { inpatient: 0.08, outpatient: 0.92 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.2174 },
      ambulatory: { parent: "outpatient", share: 0.3261 },
      radiology: { parent: "outpatient", share: 0.0761 },
    },
    specialty_mix: {
      primary_care: 0.45,
      radiology: 0.08,
      surgery: 0.05,
      emergency_medicine: 0.22,
      cardiology: 0.05,
      oncology: 0.02,
      orthopedics: 0.03,
      other: 0.1,
    },
    payer_mix: {
      medicare_ffs: 0.35,
      medicare_advantage: 0.15,
      commercial: 0.2,
      medicaid: 0.18,
      medicaid_managed: 0.05,
      self_pay: 0.07,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.06, avg_cost: 58000 },
      pa_staff: { ftes_per_1000_encounters: 0.02, avg_cost: 48000 },
      call_center_agent: { ftes_per_1000_encounters: 0.03, avg_cost: 38000 },
      physician: { ftes_per_1000_encounters: 0.25, avg_cost: 280000 },
      nurse: { ftes_per_1000_encounters: 0.9, avg_cost: 72000 },
    },
  },

  physician_group: {
    primary_volume_distribution: { inpatient: 0.0, outpatient: 1.0 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.0 },
      ambulatory: { parent: "outpatient", share: 0.5 },
      radiology: { parent: "outpatient", share: 0.1 },
    },
    specialty_mix: {
      primary_care: 0.5,
      radiology: 0.08,
      surgery: 0.05,
      emergency_medicine: 0.0,
      cardiology: 0.1,
      oncology: 0.05,
      orthopedics: 0.07,
      other: 0.15,
    },
    payer_mix: {
      medicare_ffs: 0.2,
      medicare_advantage: 0.2,
      commercial: 0.4,
      medicaid: 0.1,
      medicaid_managed: 0.03,
      self_pay: 0.07,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.04, avg_cost: 62000 },
      pa_staff: { ftes_per_1000_encounters: 0.015, avg_cost: 50000 },
      call_center_agent: { ftes_per_1000_encounters: 0.035, avg_cost: 40000 },
      physician: { ftes_per_1000_encounters: 0.3, avg_cost: 320000 },
      nurse: { ftes_per_1000_encounters: 0.5, avg_cost: 75000 },
    },
  },

  specialty_hospital: {
    primary_volume_distribution: { inpatient: 0.15, outpatient: 0.85 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.0588 },
      ambulatory: { parent: "outpatient", share: 0.4118 },
      radiology: { parent: "outpatient", share: 0.2353 },
    },
    specialty_mix: {
      primary_care: 0.05,
      radiology: 0.2,
      surgery: 0.25,
      emergency_medicine: 0.05,
      cardiology: 0.15,
      oncology: 0.1,
      orthopedics: 0.1,
      other: 0.1,
    },
    payer_mix: {
      medicare_ffs: 0.25,
      medicare_advantage: 0.2,
      commercial: 0.35,
      medicaid: 0.1,
      medicaid_managed: 0.03,
      self_pay: 0.07,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.055, avg_cost: 72000 },
      pa_staff: { ftes_per_1000_encounters: 0.028, avg_cost: 56000 },
      call_center_agent: { ftes_per_1000_encounters: 0.032, avg_cost: 43000 },
      physician: { ftes_per_1000_encounters: 0.22, avg_cost: 380000 },
      nurse: { ftes_per_1000_encounters: 0.7, avg_cost: 82000 },
    },
  },

  safety_net_hospital: {
    primary_volume_distribution: { inpatient: 0.08, outpatient: 0.92 },
    sub_breakdown_distribution: {
      emergency: { parent: "outpatient", share: 0.1957 },
      ambulatory: { parent: "outpatient", share: 0.3696 },
      radiology: { parent: "outpatient", share: 0.1304 },
    },
    specialty_mix: {
      primary_care: 0.3,
      radiology: 0.08,
      surgery: 0.07,
      emergency_medicine: 0.18,
      cardiology: 0.06,
      oncology: 0.03,
      orthopedics: 0.04,
      other: 0.24,
    },
    payer_mix: {
      medicare_ffs: 0.18,
      medicare_advantage: 0.12,
      commercial: 0.18,
      medicaid: 0.3,
      medicaid_managed: 0.12,
      self_pay: 0.1,
    },
    workforce_ratios: {
      coder: { ftes_per_1000_encounters: 0.05, avg_cost: 62000 },
      pa_staff: { ftes_per_1000_encounters: 0.028, avg_cost: 52000 },
      call_center_agent: { ftes_per_1000_encounters: 0.04, avg_cost: 42000 },
      physician: { ftes_per_1000_encounters: 0.18, avg_cost: 290000 },
      nurse: { ftes_per_1000_encounters: 0.8, avg_cost: 76000 },
    },
  },
};

// ── Derivation Function ──────────────────────────────────────

export function deriveProfileData(
  orgType: OrgType,
  totalEncounters: number,
  bedCount: number,
  userOverrides: Partial<ProfileData>,
  activeSubBreakdowns?: string[],
): { profile: OrganizationProfileData; derivations: DerivationExplanation[] } {
  const archetype = ARCHETYPES[orgType];
  const explanations: DerivationExplanation[] = [];
  // When an explicit active set is supplied, sub-breakdowns the user has
  // deleted are neither generated nor carried through from overrides.
  const isActiveSub = (key: string) =>
    activeSubBreakdowns == null || activeSubBreakdowns.includes(key);

  // 1a. Primary buckets (inpatient + outpatient) from archetype, scaled by
  //     total. These partition the total, so they sum to it.
  const encounter_volumes: Record<string, number> = {};
  for (const [setting, pct] of Object.entries(
    archetype.primary_volume_distribution,
  )) {
    const derived = Math.round(totalEncounters * pct);
    encounter_volumes[setting] = derived;
    explanations.push({
      field: `encounter_volumes.${setting}`,
      value: derived,
      explanation: `${(pct * 100).toFixed(0)}% of ${totalEncounters.toLocaleString()} total (${orgType} default)`,
    });
  }

  // 1b. Sub-breakdowns generated independently as subsets of their parent
  //     bucket — they do not add to the total. Sub-breakdowns the user has
  //     deleted are skipped so they never reach the stored profile.
  for (const [sub, { parent, share }] of Object.entries(
    archetype.sub_breakdown_distribution,
  )) {
    if (!isActiveSub(sub)) continue;
    const parentVolume = encounter_volumes[parent] ?? 0;
    const derived = Math.round(parentVolume * share);
    encounter_volumes[sub] = derived;
    explanations.push({
      field: `encounter_volumes.${sub}`,
      value: derived,
      explanation: `${(share * 100).toFixed(0)}% of ${parent} (${orgType} default)`,
    });
  }

  // 2. Bed-count-based inpatient override
  if (
    bedCount > 0 &&
    !hasEncounterVolumeInput(userOverrides.encounter_volumes, "inpatient")
  ) {
    const ipFromBeds = bedCount * 90;
    encounter_volumes.inpatient = ipFromBeds;
    explanations.push({
      field: "encounter_volumes.inpatient",
      value: ipFromBeds,
      explanation: `${bedCount} beds x 90 admissions/bed/year`,
    });
  }

  // 3. Workforce derived from encounter volume
  const workforce: ProfileData["workforce"] = {};
  for (const [role, ratio] of Object.entries(archetype.workforce_ratios)) {
    const ftes =
      Math.round(
        (totalEncounters / 1000) * ratio.ftes_per_1000_encounters * 10,
      ) / 10;
    workforce[role] = {
      ftes,
      avg_fully_loaded_cost: ratio.avg_cost,
    };
    explanations.push({
      field: `workforce.${role}.ftes`,
      value: ftes,
      explanation: `${ratio.ftes_per_1000_encounters} per 1,000 encounters x ${(totalEncounters / 1000).toFixed(0)}K`,
    });
  }

  // 4. Apply user overrides (anything explicitly set takes precedence)
  const resolvedEncounterVolumes = applyEncounterVolumeInputs({
    baseVolumes: encounter_volumes,
    encounterVolumeInputs: userOverrides.encounter_volumes,
    totalEncounters,
  });
  // Strip any deleted sub-breakdown that may still linger in stored overrides
  // so it never leaks into the persisted profile or downstream consumers.
  for (const key of Object.keys(resolvedEncounterVolumes)) {
    if (!isPrimaryEncounterKey(key) && !isActiveSub(key)) {
      delete resolvedEncounterVolumes[key];
    }
  }
  const profile: OrganizationProfileData = {
    encounter_volumes:
      resolvedEncounterVolumes as ProfileData["encounter_volumes"],
    specialty_mix: userOverrides.specialty_mix ?? archetype.specialty_mix,
    payer_mix: (userOverrides.payer_mix ??
      archetype.payer_mix) as ProfileData["payer_mix"],
    workforce: userOverrides.workforce ?? workforce,
    baselineRecords: [], // populated by the estimation engine
  };

  return { profile, derivations: explanations };
}
